/**
 * Bottom-right tool column.
 * Embed blocks pull an existing plugin button into the column.
 * The column slides away while the page is scrolling.
 */
class FloatingRail extends HTMLElement {
  connectedCallback() {
    if (this._mounted) return;
    this._mounted = true;
    this._homes = new Map();
    this._scrollActive = false;
    this._hovering = false;
    this._onScroll = this._onScroll.bind(this);
    this._onEnter = this._onEnter.bind(this);
    this._onLeave = this._onLeave.bind(this);
    this._onFocus = this._onFocus.bind(this);
    this._syncTop = this._syncTop.bind(this);
    window.addEventListener('scroll', this._onScroll, { passive: true });
    window.addEventListener('scroll', this._syncTop, { passive: true });
    this._onDismissTip = this._onDismissTip.bind(this);
    this._onTipOut = this._onTipOut.bind(this);
    this.addEventListener('pointerenter', this._onEnter);
    this.addEventListener('pointerleave', this._onLeave);
    this.addEventListener('pointerout', this._onTipOut);
    this.addEventListener('focusin', this._onFocus);
    this.addEventListener('click', this._onClick.bind(this));
    window.addEventListener('pointerdown', this._onDismissTip, true);
    this._observer = new MutationObserver((records) => {
      const outside = records.some((record) => !this.contains(record.target));
      if (!outside) return;
      if (this._syncTimer) return;
      this._syncTimer = window.setTimeout(() => {
        this._syncTimer = 0;
        this._sync();
      }, 100);
    });
    this._observer.observe(document.body, { childList: true, subtree: true });
    this._revealTimer = window.setTimeout(() => this._reveal(), 3000);
    this._onSize = () => this._syncSize();
    this._sizeQuery = window.matchMedia('(max-width: 767px)');
    this._sizeQuery.addEventListener('change', this._onSize);
    this._sync();
    this._syncTop();
  }

  disconnectedCallback() {
    this._mounted = false;
    window.removeEventListener('scroll', this._onScroll);
    window.removeEventListener('scroll', this._syncTop);
    this.removeEventListener('pointerenter', this._onEnter);
    this.removeEventListener('pointerleave', this._onLeave);
    this.removeEventListener('pointerout', this._onTipOut);
    this.removeEventListener('focusin', this._onFocus);
    window.removeEventListener('pointerdown', this._onDismissTip, true);
    this._sizeQuery?.removeEventListener('change', this._onSize);
    this._observer?.disconnect();
    clearTimeout(this._hideTimer);
    clearTimeout(this._showTimer);
    clearTimeout(this._syncTimer);
    clearTimeout(this._revealTimer);
    this._homes.forEach((home, node) => {
      if (home.next && home.next.parentNode === home.parent) home.parent.insertBefore(node, home.next);
      else home.parent?.appendChild(node);
    });
  }

  /**
   * @param {MouseEvent} event
   */
  _onClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    const top = target ? target.closest('[data-floating-rail-top]') : null;
    if (top && this.contains(top)) {
      event.preventDefault();
      this._scrollToTop(top.dataset.forceAnchor === 'true');
      return;
    }
    const survey = target ? target.closest('[data-floating-rail-survey]') : null;
    if (survey && this.contains(survey)) {
      event.preventDefault();
      this._toggleSurvey();
      return;
    }
    const shulex = target ? target.closest('[data-shulex-chat-launcher]') : null;
    if (shulex && this.contains(shulex)) {
      event.preventDefault();
      this._toggleShulex(shulex);
      return;
    }
    this._openGift(target);
  }

  /**
   * Moving the Klaviyo teaser drops its own click listener.
   * The form id is the suffix of `kl-teaser-<id>`.
   * @param {Element | null} target
   */
  _openGift(target) {
    const teaser = target ? target.closest('[class*="kl-teaser-"]') : null;
    if (!(teaser instanceof HTMLElement) || !this.contains(teaser)) return;
    const matched = String(teaser.className).match(/kl-teaser-([A-Za-z0-9]+)/);
    if (!matched) return;
    const queue = window._klOnsite;
    if (queue && typeof queue.push === 'function') {
      queue.push(['openForm', matched[1]]);
      return;
    }
    window._klOnsite = [['openForm', matched[1]]];
  }

  /**
   * The chat window stays inside the plugin host. A transform on the column
   * would pin that window to the column, so leave the column in place while
   * the window is open.
   * @returns {boolean}
   */
  _chatOpen() {
    const panel = document.getElementById('lydaas-chat');
    if (panel?.classList.contains('is-open') === true) return true;
    return this.querySelector('[data-shulex-chat-launcher][aria-expanded="true"]') instanceof HTMLElement;
  }

  /**
   * The rail button is the visible control. Shulex keeps its own launcher
   * off-screen, and a click on that launcher toggles the widget.
   * @param {HTMLElement} button
   */
  _toggleShulex(button) {
    const nextOpen = button.getAttribute('aria-expanded') !== 'true';
    button.setAttribute('aria-expanded', nextOpen ? 'true' : 'false');
    button.querySelector('[data-shulex-chat-icon="open"]')?.toggleAttribute('hidden', nextOpen);
    button.querySelector('[data-shulex-chat-icon="close"]')?.toggleAttribute('hidden', !nextOpen);
    if (nextOpen) this._clickShulexLauncher();
    else this._foldShulex();
  }

  /**
   * Shulex's own bubble only opens the window. Fold is the control that closes it.
   */
  _foldShulex() {
    const host = document.querySelector('shulex-chatbot-lancher');
    const fold = host?.shadowRoot?.querySelector('svg[aria-label="Fold"]');
    if (fold instanceof Element) {
      fold.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, composed: true, view: window }));
      return;
    }
    this._clickShulexLauncher();
  }

  /**
   * @returns {HTMLElement | null}
   */
  _shulexLauncherButton() {
    const host = document.querySelector('shulex-chatbot-lancher');
    if (!(host instanceof HTMLElement)) return null;
    const button = host.shadowRoot?.querySelector('#livechat_launcher_btn') || host.querySelector('#livechat_launcher_btn');
    if (!(button instanceof HTMLElement)) return null;
    this._parkShulexLauncher(host, button);
    return button;
  }

  /**
   * Collapse the plugin host without clipping a fixed chat window inside it.
   */
  _parkShulexHost() {
    if (!this.querySelector('[data-shulex-chat-launcher]')) return;
    const host = document.querySelector('shulex-chatbot-lancher');
    if (!(host instanceof HTMLElement)) return;
    const button = host.shadowRoot?.querySelector('#livechat_launcher_btn') || host.querySelector('#livechat_launcher_btn');
    if (button instanceof HTMLElement) this._parkShulexLauncher(host, button);
    else {
      host.style.setProperty('position', 'fixed', 'important');
      host.style.setProperty('width', '0', 'important');
      host.style.setProperty('height', '0', 'important');
      host.style.setProperty('overflow', 'visible', 'important');
    }
  }

  /**
   * Keep Shulex's own bubble out of the column. The chat window stays in the host.
   * @param {HTMLElement} host
   * @param {HTMLElement} button
   */
  _parkShulexLauncher(host, button) {
    host.style.setProperty('position', 'fixed', 'important');
    host.style.setProperty('right', '0', 'important');
    host.style.setProperty('bottom', '0', 'important');
    host.style.setProperty('width', '0', 'important');
    host.style.setProperty('height', '0', 'important');
    host.style.setProperty('margin', '0', 'important');
    host.style.setProperty('overflow', 'visible', 'important');
    host.style.setProperty('z-index', '2147483001', 'important');
    button.style.setProperty('opacity', '0', 'important');
    button.style.setProperty('pointer-events', 'none', 'important');
    button.style.setProperty('width', '1px', 'important');
    button.style.setProperty('height', '1px', 'important');
  }

  _clickShulexLauncher() {
    const ready = this._shulexLauncherButton();
    if (ready) {
      window.clearInterval(this._shulexWait);
      this._shulexWait = 0;
      ready.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, composed: true, view: window }));
      return;
    }
    if (this._shulexWait) return;
    const started = Date.now();
    this._shulexWait = window.setInterval(() => {
      const button = this._shulexLauncherButton();
      if (button) {
        window.clearInterval(this._shulexWait);
        this._shulexWait = 0;
        button.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, composed: true, view: window }));
        return;
      }
      if (Date.now() - started > 8000) {
        window.clearInterval(this._shulexWait);
        this._shulexWait = 0;
      }
    }, 200);
  }

  _onEnter() {
    this._hovering = true;
    clearTimeout(this._hideTimer);
    this.classList.remove('is-away');
  }

  _onLeave() {
    this._hovering = false;
  }

  /**
   * A click keeps the pointer over the button, so the hover tip would stay.
   * Window capture runs before the chat plugin stops the event.
   * @param {PointerEvent} event
   */
  _onDismissTip(event) {
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
    const fromPath = path.find((node) => node instanceof Element && node.hasAttribute('data-floating-rail-item'));
    const target = event.target instanceof Element ? event.target : null;
    const item = fromPath || (target ? target.closest('[data-floating-rail-item]') : null);
    if (!(item instanceof HTMLElement) || !this.contains(item)) return;
    item.classList.add('is-tip-dismissed');
    const active = document.activeElement;
    if (active instanceof HTMLElement && item.contains(active)) active.blur();
  }

  /**
   * @param {PointerEvent} event
   */
  _onTipOut(event) {
    const target = event.target instanceof Element ? event.target : null;
    const item = target ? target.closest('[data-floating-rail-item]') : null;
    if (!(item instanceof HTMLElement) || item.contains(event.relatedTarget)) return;
    if (item.matches(':hover')) return;
    item.classList.remove('is-tip-dismissed');
  }

  _onFocus() {
    clearTimeout(this._hideTimer);
    this.classList.remove('is-away');
  }

  _onScroll() {
    if (!this._revealed || performance.now() < this._scrollArmedAt) return;
    if (this._chatOpen()) {
      this.classList.remove('is-away');
      return;
    }
    if (this._hovering || this.contains(document.activeElement)) return;
    if (!this._scrollActive) {
      this._scrollActive = true;
      clearTimeout(this._hideTimer);
      this._hideTimer = window.setTimeout(() => {
        if (this._scrollActive && !this._hovering) this.classList.add('is-away');
      }, 200);
    }
    clearTimeout(this._showTimer);
    this._showTimer = window.setTimeout(() => {
      this._scrollActive = false;
      this.classList.remove('is-away');
    }, 1000);
  }

  _syncTop() {
    const item = this.querySelector('[data-kind="back-to-top"]');
    if (!(item instanceof HTMLElement)) return;
    const onSecondScreen = window.scrollY > window.innerHeight;
    item.hidden = !onSecondScreen || !this._allowed(item);
  }

  _sync() {
    this.querySelectorAll('[data-floating-rail-item]').forEach((item) => {
      if (!(item instanceof HTMLElement)) return;
      if (item.dataset.kind === 'embed') this._adopt(item);
      if (item.dataset.kind === 'member') this._adoptMember(item);
      if (item.dataset.kind === 'back-to-top') return;
      const slot = item.querySelector('[data-floating-rail-slot]');
      const filled = slot && [...slot.children].some((child) => child instanceof HTMLElement && !child.hasAttribute('data-floating-rail-survey'));
      const waiting = item.dataset.kind === 'embed' && slot && !filled;
      item.hidden = waiting || !this._allowed(item);
    });
    this._parkShulexHost();
    this._syncTop();
  }

  /**
   * Slide the settled column in once. Scroll-hide stays off briefly so a
   * load-time scroll cannot send it away again.
   */
  _reveal() {
    if (this._revealed || !this._mounted) return;
    this._sync();
    this._revealed = true;
    clearTimeout(this._revealTimer);
    this._scrollArmedAt = performance.now() + 1000;
    this.classList.add('is-holding');
    void this.offsetWidth;
    this.classList.remove('is-pending');
    void this.offsetWidth;
    window.setTimeout(() => {
      if (!this.isConnected) return;
      window.__floatingRailShown = true;
      this.classList.remove('is-holding');
      this._releaseVisibility();
    }, 32);
  }

  /**
   * @param {Element} node
   */
  _clearVisibility(node) {
    const clear = (el) => {
      if (!(el instanceof HTMLElement)) return;
      if (el.style.getPropertyValue('visibility') !== 'hidden') return;
      el.style.removeProperty('visibility');
    };
    if (node instanceof HTMLElement) clear(node);
    if (node && node.querySelectorAll) node.querySelectorAll('*').forEach(clear);
  }

  /**
   * Inline visibility was only there so a plugin could not paint early.
   * The column is on screen now, so let the stylesheet show the buttons.
   */
  _releaseVisibility() {
    this._clearVisibility(this);
  }

  /**
   * @param {HTMLElement} item
   * @returns {boolean}
   */
  _allowed(item) {
    const mode = item.dataset.visibility || 'all';
    if (mode === 'all') return true;
    const path = window.location.pathname;
    let matched = false;
    (item.dataset.patterns || '').split(';').forEach((part) => {
      const source = part.trim();
      if (!source) return;
      try {
        if (new RegExp(source).test(path)) matched = true;
      } catch (error) {
        // A broken token does not hide the whole column.
      }
    });
    return mode === 'include' ? matched : !matched;
  }

  /**
   * @param {HTMLElement} item
   */
  _adopt(item) {
    const slot = item.querySelector('[data-floating-rail-slot]');
    if (!slot) return;
    if (item.dataset.selector === '#zigpoll-launcher-frame') {
      if (!slot.querySelector('#zigpoll-launcher-frame')) {
        const launcher = this._find(item.dataset.selector);
        if (launcher && !this.contains(launcher)) this._place(launcher, slot);
      }
      this._hideSurveyClose();
      return;
    }
    if (slot.firstElementChild) return;
    const node = this._teaserShell(this._find(item.dataset.selector || ''));
    if (!node || this.contains(node)) return;
    this._place(node, slot);
  }

  /**
   * The launcher iframe keeps Zigpoll's own blue pill. The rail button is the
   * visible control, so the iframe only has to stay in the slot.
   */
  _toggleSurvey() {
    const api = window.Zigpoll;
    if (!api) return;
    if (typeof api.toggle === 'function') api.toggle();
    else if (typeof api.close === 'function' && document.querySelector('#zigpoll-embed-wrapper.zigpoll-active')) api.close();
    else if (typeof api.open === 'function') api.open();
  }

  /**
   * The launcher X is Zigpoll's dismiss control. The rail icon is the only control.
   */
  _hideSurveyClose() {
    const close = document.getElementById('zigpoll-hide-frame');
    if (!(close instanceof HTMLElement) || close.dataset.railHiding === '1') return;
    const hide = () => {
      close.dataset.railHiding = '1';
      close.style.setProperty('display', 'none', 'important');
      close.style.setProperty('visibility', 'hidden', 'important');
      close.style.setProperty('pointer-events', 'none', 'important');
      close.dataset.railHiding = '0';
    };
    hide();
    if (!close._railHideObserver) {
      const observer = new MutationObserver(hide);
      observer.observe(close, { attributes: true, attributeFilter: ['style', 'class'] });
      close._railHideObserver = observer;
    }
  }

  /**
   * @param {HTMLElement} item
   */
  _adoptMember(item) {
    const slot = item.querySelector('[data-floating-rail-slot]');
    const button = document.querySelector('[data-member-reward-toggle]');
    if (!slot || !button || slot.contains(button)) return;
    this._place(button, slot);
  }

  /**
   * @param {string} selector
   * @returns {Element | null}
   */
  _find(selector) {
    const sources = selector.split('||').map((part) => part.trim()).filter(Boolean);
    for (const source of sources) {
      const found = document.querySelector(source) || this._findInShadow(source);
      if (found) return found;
    }
    return null;
  }

  /**
   * @param {string} selector
   * @returns {Element | null}
   */
  _findInShadow(selector) {
    const hosts = document.querySelectorAll('*');
    for (const host of hosts) {
      const root = host.shadowRoot;
      if (!root) continue;
      const found = root.querySelector(selector);
      if (found) return found;
    }
    return null;
  }

  /**
   * @param {Element} node
   * @param {Element} slot
   */
  _place(node, slot) {
    if (!this._homes.has(node)) {
      this._homes.set(node, { parent: node.parentElement, next: node.nextSibling });
    }
    slot.appendChild(node);
    this._pin(node, { size: node.id !== 'zigpoll-launcher-frame' });
    if (this._revealed) this._clearVisibility(node);
    else node.style.setProperty('visibility', 'hidden', 'important');
    this._watch(node);
  }

  /**
   * The gift bubble is a small fixed shell. Take that shell, not an inner icon,
   * so the close mark and the slide-up animation come with it.
   * @param {Element | null} node
   * @returns {Element | null}
   */
  _teaserShell(node) {
    let current = node instanceof HTMLElement ? node : null;
    if (!current || !String(current.className || '').includes('kl-teaser-')) return node;
    let parent = current.parentElement;
    while (parent && parent !== document.body) {
      const rect = parent.getBoundingClientRect();
      const compact = rect.width > 0 && rect.width <= 180 && rect.height > 0 && rect.height <= 180;
      const marked = /kl-teaser-|kl-private-reset|needsclick/.test(String(parent.className || ''));
      if (!compact || !marked) break;
      current = parent;
      parent = current.parentElement;
    }
    return current;
  }

  /**
   * The plugin rewrites inline position. Pin again when that happens.
   * @param {HTMLElement} node
   */
  _watch(node) {
    if (node._railStyleObserver) return;
    const observer = new MutationObserver(() => this._pin(node, { size: node.id !== 'zigpoll-launcher-frame' && node.id !== 'zigpoll-hide-frame' }));
    observer.observe(node, { attributes: true, attributeFilter: ['style', 'class'] });
    node._railStyleObserver = observer;
  }

  /**
   * Phone buttons stay 42px. Desktop stays 48px and only grows on hover.
   * @returns {'42px' | '48px'}
   */
  _buttonSize() {
    return window.matchMedia('(max-width: 767px)').matches ? '42px' : '48px';
  }

  /**
   * Plugin nodes keep an inline size, so a breakpoint change has to pin them again.
   */
  _syncSize() {
    this.querySelectorAll('#Access-Pro-Icon, #asw-icon, [class*="kl-teaser-"]').forEach((node) => this._pin(node));
  }

  /**
   * @param {Element} node
   */
  _pin(node, options = {}) {
    if (!(node instanceof HTMLElement) || node.dataset.railPinning === '1') return;
    if (node.id === 'zigpoll-hide-frame') {
      this._pinSurveyClose(node);
      return;
    }
    if (node.id === 'zigpoll-launcher-frame') {
      this._pinSurveyLauncher(node);
      return;
    }
    const size = options.size !== false;
    const buttonSize = node.id === 'asw-icon' ? '100%' : this._buttonSize();
    if (getComputedStyle(node).position === 'static' && node.style.getPropertyValue('position') === 'static' && (!size || node.style.getPropertyValue('width') === buttonSize)) return;
    node.dataset.railPinning = '1';
    const keys = ['position', 'inset', 'top', 'right', 'bottom', 'left', 'margin', 'transform', 'z-index'];
    if (size) keys.push('width', 'height', 'min-width');
    keys.forEach((key) => {
      const value = key === 'position' ? 'static' : key === 'width' || key === 'height' || key === 'min-width' ? buttonSize : key === 'margin' ? '0' : key === 'transform' ? 'none' : 'auto';
      if (key === 'z-index') {
        node.style.setProperty(key, 'auto', 'important');
        return;
      }
      node.style.setProperty(key, value, 'important');
    });
    node.dataset.railPinning = '0';
  }

  /**
   * Cover the rail button without painting Zigpoll's pill or the open-state check.
   * @param {HTMLElement} node
   */
  _pinSurveyLauncher(node) {
    const style = node.style;
    if (style.getPropertyValue('position') === 'absolute' && style.getPropertyValue('opacity') === '0' && style.getPropertyValue('width') === '100%') return;
    node.dataset.railPinning = '1';
    const set = (key, value) => node.style.setProperty(key, value, 'important');
    set('position', 'absolute');
    set('top', '0');
    set('right', '0');
    set('bottom', '0');
    set('left', '0');
    set('width', '100%');
    set('height', '100%');
    set('max-width', 'none');
    set('min-width', '0');
    set('margin', '0');
    set('transform', 'none');
    set('opacity', '0');
    set('pointer-events', 'none');
    node.dataset.railPinning = '0';
  }

  /**
   * Keep the dismiss mark on the pill. Leave transform alone so Zigpoll can
   * hide it while the survey is open.
   * @param {HTMLElement} node
   */
  _pinSurveyClose(node) {
    const style = node.style;
    if (style.getPropertyValue('position') === 'absolute' && style.getPropertyValue('top') === '-6px' && style.getPropertyValue('width') === '18px') return;
    node.dataset.railPinning = '1';
    const set = (key, value) => node.style.setProperty(key, value, 'important');
    set('position', 'absolute');
    set('top', '-6px');
    set('right', '0');
    set('bottom', 'auto');
    set('left', 'auto');
    set('margin', '0');
    set('width', '18px');
    set('height', '18px');
    set('min-width', '0');
    set('z-index', '2');
    node.dataset.railPinning = '0';
  }

  /**
   * Keep the existing back-to-top motion. ScrollTrigger snap rewrites scroll
   * position, so a native smooth scroll stops halfway.
   * @param {boolean} forceAnchor
   */
  _scrollToTop(forceAnchor) {
    if (forceAnchor) {
      window.scrollTo({ top: 0, behavior: 'auto' });
      window.setTimeout(() => window.scrollTo({ top: 0, behavior: 'auto' }), 120);
      window.setTimeout(() => window.scrollTo({ top: 0, behavior: 'auto' }), 260);
      window.setTimeout(() => window.scrollTo({ top: 0, behavior: 'auto' }), 420);
      return;
    }
    if (window.ScrollTrigger) {
      window.ScrollTrigger.getAll().forEach((trigger) => {
        if (!trigger.vars || !trigger.vars.snap || typeof trigger.getTween !== 'function') return;
        const snapTween = trigger.getTween(true);
        if (snapTween && typeof snapTween.kill === 'function') snapTween.kill();
      });
    }
    const start = window.scrollY;
    if (start <= 0) return;
    const duration = Math.min(Math.max(start / 20, 250), 600);
    const startTime = performance.now();
    const step = (now) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const eased = 1 - (1 - progress) ** 3;
      window.scrollTo(0, Math.round(start * (1 - eased)));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }
}

if (!customElements.get('floating-rail')) {
  customElements.define('floating-rail', FloatingRail);
}
